import React, { useCallback, useRef, useEffect } from 'react'

function autoResize(textarea) {
  if (!textarea) return
  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

function getCounterState(count, max) {
  if (count >= max) return 'danger'
  if (count >= max * 0.85) return 'warning'
  return 'safe'
}

function MePill({ bg, color }) {
  return (
    <div
      style={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 8,
        color: color,
        fontWeight: 500,
        flexShrink: 0,
      }}
    >
      me
    </div>
  )
}

function ArrowRight({ color, strokeWidth }) {
  return (
    <svg
      width="18"
      height="10"
      viewBox="0 0 18 10"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M0 5 L15 5 M11 1 L15 5 L11 9"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function DirectionButton({ direction, selected, disabled, onClick }) {
  const isGive = direction === 'give'
  const label = isGive ? 'Give' : 'Receive'

  const mePillColor = selected ? '#185FA5' : 'white'
  const mePillBg = selected ? 'white' : '#B4B2A9'
  const arrowColor = selected ? 'white' : '#5F5E5A'
  const arrowWidth = selected ? 2 : 1.5
  const labelColor = selected ? 'white' : '#5F5E5A'

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1,
        padding: '10px 12px',
        background: selected ? '#185FA5' : '#f1efe8',
        border: `0.5px solid ${selected ? '#185FA5' : '#e0ddd4'}`,
        borderRadius: 8,
        cursor: disabled ? 'default' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        fontFamily: 'inherit',
        opacity: disabled ? 0.4 : 1,
        transition: 'background var(--duration-fast) ease, border var(--duration-fast) ease, color var(--duration-fast) ease',
      }}
      aria-label={`${label} feedback${selected ? ', selected' : ''}`}
      aria-pressed={selected}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {isGive ? (
          <>
            <MePill bg={mePillBg} color={mePillColor} />
            <ArrowRight color={arrowColor} strokeWidth={arrowWidth} />
          </>
        ) : (
          <>
            <ArrowRight color={arrowColor} strokeWidth={arrowWidth} />
            <MePill bg={mePillBg} color={mePillColor} />
          </>
        )}
      </div>
      <span style={{ fontSize: 12, fontWeight: 500, color: labelColor, whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </button>
  )
}

function ContextBlock({ direction, context, maxChars, disabled, onChange }) {
  const textareaRef = useRef(null)
  const label = direction === 'give'
    ? 'What do you want to raise?'
    : 'What do you want to hear about?'

  const placeholder = direction === 'give'
    ? 'The topic you want to bring up…'
    : 'The topic you want their read on…'

  const charCount = context.length
  const counterState = getCounterState(charCount, maxChars)
  const counterColor = {
    safe: 'var(--color-text-tertiary)',
    warning: '#BA7517',
    danger: '#A32D2D',
  }[counterState]

  useEffect(() => {
    if (textareaRef.current) autoResize(textareaRef.current)
  }, [context])

  return (
    <div style={{ borderLeft: '2px solid #185FA5', paddingLeft: 10 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            color: '#185FA5',
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: 10,
            color: counterColor,
            fontVariantNumeric: 'tabular-nums',
          }}
          aria-live="polite"
        >
          {charCount} / {maxChars}
        </span>
      </div>

      <textarea
        ref={textareaRef}
        value={context}
        onChange={(e) => {
          const next = e.target.value
          if (next.length <= maxChars) {
            onChange(next)
          } else {
            onChange(next.slice(0, maxChars))
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        aria-label={label}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          outline: 'none',
          resize: 'none',
          fontFamily: 'inherit',
          fontSize: 12,
          lineHeight: 1.5,
          color: 'var(--color-text-primary)',
          padding: 0,
          overflowY: 'hidden',
          minHeight: '18px',
        }}
        onInput={(e) => autoResize(e.target)}
      />
    </div>
  )
}

function EmptyHint() {
  return (
    <div
      style={{
        padding: '10px 14px',
        background: '#f1efe8',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div
        style={{
          width: 6,
          height: 24,
          background: '#B4B2A9',
          borderRadius: 3,
          flexShrink: 0,
        }}
      />
      <div
        style={{
          fontSize: 12,
          color: 'var(--color-text-secondary)',
          fontStyle: 'italic',
        }}
      >
        Pick a direction to continue
      </div>
    </div>
  )
}

export function FeedbackDirectionToggle({ value, onChange, maxContextChars = 200, disabled = false }) {
  const dir = value?.direction ?? null
  const ctx = value?.context ?? ''

  const handleDirectionClick = useCallback((direction) => {
    if (disabled) return
    if (dir === direction) {
      onChange({ direction: null, context: ctx })
    } else {
      onChange({ direction, context: ctx })
    }
  }, [dir, ctx, disabled, onChange])

  return (
    <div style={{ padding: '10px 14px 12px' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <DirectionButton
          direction="give"
          selected={dir === 'give'}
          disabled={disabled}
          onClick={() => handleDirectionClick('give')}
        />
        <DirectionButton
          direction="receive"
          selected={dir === 'receive'}
          disabled={disabled}
          onClick={() => handleDirectionClick('receive')}
        />
      </div>

      {dir ? (
        <ContextBlock
          direction={dir}
          context={ctx}
          maxChars={maxContextChars}
          disabled={disabled}
          onChange={(newContext) => onChange({ direction: dir, context: newContext })}
        />
      ) : (
        <EmptyHint />
      )}
    </div>
  )
}
